/**
 * The pure heart of the checkout poll loop, lifted out of the hook so it can be
 * unit-tested without a component. Ported verbatim from
 * `asthra-v4-web/src/new-document/use-checkout.ts`.
 */

import type { PaymentIntent } from "../../api/client";

/**
 * How often to ask the provider, and how much that slows down. Every poll is a
 * call to the provider (not our own record — a task-pane has no webhook URL), so
 * it starts fast for the card-typed-in-20-seconds case and backs off toward the
 * cap for the walked-away case.
 */
const POLL_MIN_MS = 2_500;
const POLL_MAX_MS = 15_000;
const POLL_GROWTH = 1.4;

/** How long to wait before the nth poll. Pure, so the curve is testable. */
export function pollDelay(attempt: number): number {
  return Math.min(POLL_MIN_MS * POLL_GROWTH ** attempt, POLL_MAX_MS);
}

/** What a poll's answer means. */
export type PollOutcome =
  | { readonly kind: "settle" }
  | { readonly kind: "refused"; readonly reason: "failed" | "expired"; readonly detail: string | null }
  | { readonly kind: "wait" };

/**
 * Read one poll of an intent, and decide whether to keep waiting.
 *
 * The expiry is read from the intent, not a handoff, and the server is not asked
 * to agree first — it marks an intent expired lazily, so for up to an hour the
 * record still says `created` while the checkout link has stopped working.
 */
export function readIntent(intent: PaymentIntent, now: number): PollOutcome {
  if (intent.state === "completed") return { kind: "settle" };
  if (intent.state === "failed" || intent.state === "expired") {
    return { kind: "refused", reason: intent.state, detail: intent.failure_reason };
  }
  const expiry = Date.parse(intent.expires_at);
  if (Number.isFinite(expiry) && now > expiry) {
    return { kind: "refused", reason: "expired", detail: null };
  }
  return { kind: "wait" };
}

/**
 * Whether a browser could actually navigate to this. Tested on the SCHEME, so a
 * fake provider's `fake://checkout/<ref>` is caught whoever produced it.
 */
export function isOpenable(url: string): boolean {
  return url.startsWith("https://") || url.startsWith("http://");
}
